


























































































































































































































import { Component, Vue } from 'vue-property-decorator';
import {
    fetchNetworkBuckets,
    fetchNetworkDaily,
    fetchNetworkIncidents,
    fetchNetworkStatus,
    fetchNetworkSummary,
    INCIDENT_SCOPE_MEANING,
    NetworkBucket,
    NetworkDailyRow,
    NetworkIncident,
    NetworkStatus,
} from '@/api/network';
import { networkProbe } from '@/utils/network/instance';
import { LiveState } from '@/utils/network/probe';
import {
    LEVEL_CLASS,
    LEVEL_LABEL,
    LEVEL_MEANING,
    latencyWord,
    level,
    stabilityWord,
} from '@/utils/network/score';

interface Metric {
    label: string;
    value: string;
    hint?: string;
}

@Component
export default class Network extends Vue {
    live: LiveState | null = null;

    buckets: NetworkBucket[] = [];

    status: NetworkStatus | null = null;

    incidents: NetworkIncident[] = [];

    loadingIncidents = true;

    rows: NetworkDailyRow[] = [];

    days: string[] = [];

    selectedDay = '';

    minMinutes = 30;

    loadingBuckets = true;

    loadingDay = true;

    private unsubscribe: (() => void) | null = null;

    mounted() {
        const probe = networkProbe();

        if (probe) {
            this.unsubscribe = probe.onChange((state) => {
                this.live = state;
            });
        }

        this.buildDays();
        this.loadBuckets();
        this.loadDay();
        this.loadSummary();
        this.loadStatus();
        this.loadIncidents();
    }

    beforeDestroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    get levelLabel(): string {
        return LEVEL_LABEL[level(this.live ? this.live.score : null)];
    }

    get levelClass(): string {
        return LEVEL_CLASS[level(this.live ? this.live.score : null)];
    }

    get levelMeaning(): string {
        return LEVEL_MEANING[level(this.live ? this.live.score : null)];
    }

    get udpError(): string | null {
        if (!this.live?.udpEnabled) {
            return null;
        }

        return this.live.udp && !this.live.udp.ok ? this.live.udp.error : null;
    }

    get metrics(): Metric[] {
        const { live: state } = this as { live: LiveState };
        const { udp } = state;
        const enlace = state.effectiveType ? `enlace ${state.effectiveType}` : undefined;

        if (!state.udpEnabled) {
            return [
                {
                    label: 'Demora',
                    value: this.ms(state.rtt),
                    hint: latencyWord(state.rtt),
                },
                {
                    label: 'Estabilidad',
                    value: this.ms(state.jitter),
                    hint: stabilityWord(state.jitter),
                },
                { label: 'Cortes', value: String(state.timeouts) },
                { label: 'Desconexiones', value: String(state.reconnects), hint: enlace },
            ];
        }

        const jitter = udp?.ok ? udp.jitterAvg : state.jitter;
        const rtt = udp?.ok ? udp.rttAvg : state.rtt;

        return [
            {
                label: 'Audio perdido',
                value: udp?.ok ? `${udp.lossPct}%` : 'sin dato',
                hint: udp?.ok ? 'sobre el camino de la voz' : undefined,
            },
            { label: 'Estabilidad', value: this.ms(jitter), hint: stabilityWord(jitter) },
            { label: 'Demora', value: this.ms(rtt), hint: latencyWord(rtt) },
            {
                label: 'Cortes / desconexiones',
                value: `${state.timeouts} / ${state.reconnects}`,
                hint: enlace,
            },
        ];
    }

    async testNow() {
        const probe = networkProbe();

        if (probe) {
            await probe.testNow();
        }

        // la ventana en curso todavia no cerro, pero las anteriores pueden
        // haber entrado mientras el usuario miraba otra pantalla
        await this.loadBuckets();
    }

    buildDays() {
        const today = new Date();
        this.days = Array.from({ length: 30 }, (_value, index) => {
            const day = new Date(today.getTime() - index * 86400000);
            return day.toISOString().slice(0, 10);
        });
        [this.selectedDay] = this.days;
    }

    async loadBuckets() {
        this.loadingBuckets = true;
        try {
            this.buckets = await fetchNetworkBuckets(2);
        } catch (err) {
            this.buckets = [];
        } finally {
            this.loadingBuckets = false;
        }
    }

    async loadDay() {
        this.loadingDay = true;
        try {
            this.rows = await fetchNetworkDaily(this.selectedDay);
        } catch (err) {
            this.rows = [];
        } finally {
            this.loadingDay = false;
        }

        await this.loadIncidents();
    }

    async loadStatus() {
        try {
            this.status = await fetchNetworkStatus();
        } catch (err) {
            this.status = null;
        }
    }

    async loadIncidents() {
        this.loadingIncidents = true;
        try {
            this.incidents = await fetchNetworkIncidents(this.selectedDay);
        } catch (err) {
            this.incidents = [];
        } finally {
            this.loadingIncidents = false;
        }
    }

    get statusLabel(): string {
        const status = this.status as NetworkStatus;

        if (!status.users_total) {
            return 'Nadie midiendo';
        }

        return status.users_degraded ? 'Con problemas' : 'Todo bien';
    }

    get statusClass(): string {
        const status = this.status as NetworkStatus;

        if (!status.users_total) {
            return 'bg-gray-100 text-gray-600';
        }
        if (!status.users_degraded) {
            return 'bg-green-100 text-green-800';
        }

        return status.networks_degraded >= 2
            ? 'bg-red-100 text-red-800'
            : 'bg-yellow-100 text-yellow-800';
    }

    /**
     * Que se observo, no de quien es la culpa. La conclusion la saca el que
     * lee, con el contexto que nosotros no tenemos.
     */
    get statusMeaning(): string {
        const status = this.status as NetworkStatus;

        if (!status.users_total) {
            return 'Nadie tiene el panel abierto en este momento.';
        }
        if (!status.users_degraded) {
            return 'Ninguna conexión medida está degradada ahora.';
        }
        if (status.networks_degraded >= 2) {
            return 'Hay conexiones degradadas en redes distintas al mismo tiempo, '
                + 'cada una con su proveedor.';
        }

        return 'Las conexiones degradadas salen todas por la misma red.';
    }

    // eslint-disable-next-line class-methods-use-this
    scopeMeaning(incident: NetworkIncident): string {
        return INCIDENT_SCOPE_MEANING[incident.scope];
    }

    // eslint-disable-next-line class-methods-use-this
    hour(value: string): string {
        return new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    async loadSummary() {
        try {
            const summary = await fetchNetworkSummary(30);
            this.minMinutes = summary.minMinutes;
        } catch (err) {
            this.minMinutes = 30;
        }
    }

    /** El dia tiene medicion de perdida, o sea corrio la sonda UDP. */
    get showLoss(): boolean {
        return this.rows.some((row) => row.udp_loss_avg !== null
            && row.udp_loss_avg !== undefined);
    }

    get ranked(): NetworkDailyRow[] {
        return this.rows
            .filter((row) => row.minutes_measured >= this.minMinutes && row.score_avg !== null)
            .sort((a, b) => (a.score_avg as number) - (b.score_avg as number));
    }

    isWorst(row: NetworkDailyRow): boolean {
        const { ranked } = this;
        return ranked.length > 1 && ranked[0].user_email === row.user_email;
    }

    isBest(row: NetworkDailyRow): boolean {
        const { ranked } = this;
        return ranked.length > 1 && ranked[ranked.length - 1].user_email === row.user_email;
    }

    rowClass(row: NetworkDailyRow): string {
        if (this.isWorst(row)) {
            return 'bg-red-50';
        }
        if (this.isBest(row)) {
            return 'bg-green-50';
        }
        return '';
    }

    // eslint-disable-next-line class-methods-use-this
    classOf(score: number | null): string {
        return LEVEL_CLASS[level(score)];
    }

    // eslint-disable-next-line class-methods-use-this
    labelOf(score: number | null): string {
        return LEVEL_LABEL[level(score)];
    }

    // eslint-disable-next-line class-methods-use-this
    show(value: number | null): string {
        return value === null || value === undefined ? '-' : String(value);
    }

    // eslint-disable-next-line class-methods-use-this
    ms(value: number | null | undefined): string {
        return value === null || value === undefined ? '-' : `${value} ms`;
    }

    // eslint-disable-next-line class-methods-use-this
    pct(value: number | null | undefined): string {
        return value === null || value === undefined ? '-' : `${value}%`;
    }

    // eslint-disable-next-line class-methods-use-this
    barHeight(score: number | null): string {
        return `${Math.max(4, score === null ? 4 : score)}%`;
    }

    // eslint-disable-next-line class-methods-use-this
    barClass(score: number | null): string {
        const map: Record<string, string> = {
            excellent: 'bg-green-500',
            good: 'bg-teal-500',
            fair: 'bg-yellow-500',
            bad: 'bg-red-500',
            unknown: 'bg-gray-300',
        };

        return map[level(score)];
    }

    bucketTitle(bucket: NetworkBucket): string {
        const at = new Date(bucket.bucket_at).toLocaleTimeString();
        const loss = bucket.udp_probe_ok ? `${bucket.udp_loss_pct}%` : 'sin dato';

        return `${at} · puntaje ${this.show(bucket.score)} · latencia ${this.ms(bucket.rtt_avg)}`
            + ` · pérdida ${loss}`;
    }
}
